import { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, ChevronUp, ChevronDown, Music2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { cn } from '../lib/utils';

export function AudioPlayer({ track, onClose, onNext, onPrevious }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    // --- Chargement / lecture du titre courant ---
    useEffect(() => {
        if (track && audioRef.current) {
            setIsLoading(true);
            audioRef.current.pause();
            const secureUrl = track.url.replace(/^http:\/\//i, 'https://');
            audioRef.current.src = secureUrl;
            audioRef.current.load();

            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => setIsPlaying(true))
                    .catch(error => {
                        setIsLoading(false);
                        if (error.name !== 'AbortError') console.error("Playback failed", error);
                    });
            }
        }
    }, [track]);

    // --- Événements de l'élément <audio> ---
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            setProgress(audio.currentTime);
            setDuration(audio.duration || 0);
        };
        const handleEnded = () => {
            setIsPlaying(false);
            if (onNext) onNext();
        };
        const handleWaiting = () => setIsLoading(true);
        const handleCanPlay = () => setIsLoading(false);
        const handlePlaying = () => { setIsLoading(false); setIsPlaying(true); };
        const handlePause = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', updateProgress);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('waiting', handleWaiting);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('playing', handlePlaying);
        audio.addEventListener('pause', handlePause);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', updateProgress);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('playing', handlePlaying);
            audio.removeEventListener('pause', handlePause);
        };
    }, [onNext]);

    // --- Intégration MediaSession (contrôles écran verrouillé / notif Android) ---
    useEffect(() => {
        if (!('mediaSession' in navigator) || !track) return;
        try {
            navigator.mediaSession.metadata = new window.MediaMetadata({
                title: track.title || 'Lecture',
                artist: track.artist || track.author || 'Daara Wakana',
                album: track.album || 'Daara Wakana Haqqan',
                artwork: [
                    { src: track.cover || track.image || '/logo.jpg', sizes: '512x512', type: 'image/jpeg' },
                ],
            });
            const set = (action, handler) => {
                try { navigator.mediaSession.setActionHandler(action, handler); } catch { /* non supporté */ }
            };
            set('play', () => audioRef.current?.play());
            set('pause', () => audioRef.current?.pause());
            set('previoustrack', onPrevious || null);
            set('nexttrack', onNext || null);
            set('seekto', (e) => {
                if (audioRef.current && e.seekTime != null) audioRef.current.currentTime = e.seekTime;
            });
        } catch (e) {
            // MediaMetadata non supporté : on ignore silencieusement.
        }
    }, [track, onNext, onPrevious]);

    // Reflète l'état de lecture vers l'OS.
    useEffect(() => {
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
        }
    }, [isPlaying]);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
    };

    const handleSeek = (value) => {
        if (audioRef.current) {
            audioRef.current.currentTime = value[0];
            setProgress(value[0]);
        }
    };

    const handleVolumeChange = (value) => {
        const newVolume = value[0];
        setVolume(newVolume);
        if (audioRef.current) audioRef.current.volume = newVolume;
        setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
        if (audioRef.current) {
            const newMuted = !isMuted;
            setIsMuted(newMuted);
            audioRef.current.volume = newMuted ? 0 : volume;
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // --- Geste « swipe » vertical pour ouvrir/fermer le lecteur étendu ---
    const touchStartY = useRef(null);
    const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
        if (touchStartY.current == null) return;
        const delta = e.changedTouches[0].clientY - touchStartY.current;
        if (delta < -50) setIsExpanded(true);   // swipe vers le haut → étendre
        if (delta > 80) setIsExpanded(false);   // swipe vers le bas → réduire
        touchStartY.current = null;
    };

    if (!track) return null;

    const lyrics = track.text || track.lyrics || track.content || track.translation;
    const pct = duration ? (progress / duration) * 100 : 0;

    return (
        <div
            className={cn(
                "fixed left-0 right-0 z-[60] transition-all duration-300",
                isExpanded
                    ? "inset-0 bg-gradient-to-b from-background via-background to-primary/5 flex flex-col"
                    : "bottom-[calc(4rem_+_env(safe-area-inset-bottom))] md:bottom-0 mx-2 md:mx-0 rounded-2xl md:rounded-none border bg-background/90 backdrop-blur-lg shadow-2xl"
            )}
        >
            <audio ref={audioRef} preload="none" />

            {/* ====================== MINIPLAYER ====================== */}
            {!isExpanded && (
                <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                    {/* Barre de progression fine en haut */}
                    <div className="absolute top-0 left-3 right-3 md:left-0 md:right-0 h-0.5 bg-secondary/30 overflow-hidden rounded-full">
                        <div className="h-full bg-primary transition-all duration-100 ease-linear" style={{ width: `${pct}%` }} />
                    </div>

                    <div className="flex items-center gap-3 px-3 h-16 md:container md:mx-auto md:px-4">
                        {/* Infos titre — tap pour étendre */}
                        <button
                            type="button"
                            onClick={() => setIsExpanded(true)}
                            className="flex items-center gap-3 min-w-0 flex-1 text-left"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 overflow-hidden">
                                {track.cover || track.image ? (
                                    <img src={track.cover || track.image} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <Music2 className="h-5 w-5 opacity-70" />
                                )}
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-sm truncate">{track.title}</h3>
                                <p className="text-muted-foreground text-xs truncate">{track.artist || track.author}</p>
                            </div>
                        </button>

                        {/* Contrôles compacts */}
                        <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="icon" className="hidden sm:flex hover:text-primary" onClick={onPrevious} disabled={!onPrevious}>
                                <SkipBack className="h-5 w-5" />
                            </Button>
                            <Button size="icon" className="rounded-full h-10 w-10" onClick={togglePlay} disabled={isLoading}>
                                {isLoading ? (
                                    <div className="animate-spin rounded-full border-2 border-background border-t-transparent h-5 w-5" />
                                ) : isPlaying ? (
                                    <Pause className="h-5 w-5" />
                                ) : (
                                    <Play className="h-5 w-5 ml-0.5" />
                                )}
                            </Button>
                            <Button variant="ghost" size="icon" className="hover:text-primary" onClick={onNext} disabled={!onNext}>
                                <SkipForward className="h-5 w-5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="hidden md:flex text-muted-foreground" onClick={() => setIsExpanded(true)}>
                                <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="hover:bg-destructive/10 hover:text-destructive" onClick={onClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ====================== LECTEUR ÉTENDU ====================== */}
            {isExpanded && (
                <>
                    {/* En-tête : poignée + réduire */}
                    <div
                        className="flex items-center justify-between px-4 pt-3 safe-top md:pt-4"
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                    >
                        <Button variant="ghost" size="icon" onClick={() => setIsExpanded(false)}>
                            <ChevronDown className="h-6 w-6" />
                        </Button>
                        <div className="mx-auto h-1.5 w-12 rounded-full bg-muted-foreground/30" />
                        <Button variant="ghost" size="icon" onClick={onClose} className="hover:text-destructive">
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pb-8 flex flex-col items-center">
                        {/* Pochette */}
                        <div className="mt-4 w-56 h-56 sm:w-64 sm:h-64 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary shadow-xl overflow-hidden shrink-0">
                            {track.cover || track.image ? (
                                <img src={track.cover || track.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <Music2 className="h-20 w-20 opacity-30" />
                            )}
                        </div>

                        {/* Titre */}
                        <div className="text-center mt-6 max-w-xl">
                            <h2 className="text-2xl font-bold">{track.title}</h2>
                            <p className="text-muted-foreground mt-1">{track.artist || track.author}</p>
                        </div>

                        {/* Scrubber */}
                        <div className="w-full max-w-xl mt-6 flex items-center gap-3">
                            <span className="text-xs w-10 text-right tabular-nums">{formatTime(progress)}</span>
                            <Slider value={[progress]} max={duration || 0} step={1} onValueChange={handleSeek} className="flex-1 cursor-pointer" />
                            <span className="text-xs w-10 tabular-nums">{formatTime(duration)}</span>
                        </div>

                        {/* Transport */}
                        <div className="flex items-center gap-6 mt-4">
                            <Button variant="ghost" size="icon" className="hover:text-primary" onClick={onPrevious} disabled={!onPrevious}>
                                <SkipBack className="h-8 w-8" />
                            </Button>
                            <Button size="icon" className="rounded-full h-16 w-16" onClick={togglePlay} disabled={isLoading}>
                                {isLoading ? (
                                    <div className="animate-spin rounded-full border-2 border-background border-t-transparent h-6 w-6" />
                                ) : isPlaying ? (
                                    <Pause className="h-8 w-8" />
                                ) : (
                                    <Play className="h-8 w-8 ml-1" />
                                )}
                            </Button>
                            <Button variant="ghost" size="icon" className="hover:text-primary" onClick={onNext} disabled={!onNext}>
                                <SkipForward className="h-8 w-8" />
                            </Button>
                        </div>

                        {/* Volume */}
                        <div className="flex items-center gap-2 mt-4 w-full max-w-xs">
                            <Button variant="ghost" size="icon" onClick={toggleMute} className="text-muted-foreground">
                                {isMuted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                            </Button>
                            <Slider value={[isMuted ? 0 : volume]} max={1} step={0.01} onValueChange={handleVolumeChange} className="flex-1" />
                        </div>

                        {/* Texte / paroles de la Khassaide (lecture simultanée) */}
                        <div className="w-full max-w-xl mt-8 border-t pt-6">
                            <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">Texte</h3>
                            {lyrics ? (
                                <p className="whitespace-pre-wrap leading-relaxed text-foreground/90" dir="auto">{lyrics}</p>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">
                                    Le texte de cette Khassaide n'est pas encore disponible pour la lecture simultanée.
                                </p>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
